import {
	Box,
	CircularProgress,
	IconButton,
	Stack,
	Typography,
	useTheme,
} from "@mui/material";
import { formatDate } from "date-fns";
import { useAtomValue } from "jotai";
import { CircleArrowDown, Ellipsis, FileText } from "lucide-react";
import { useState } from "react";
import { useOpenDocumentMutation } from "web_myplann/Api/Document/useOpenDocumentMutation";
import type { DocumentDTO } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { DocumentActionsDrawer } from "web_myplann/Portfolio/Components/Document/DocumentActionsDrawer";
import { downloadedDocumentsState } from "web_myplann/Portfolio/State/downloadedDocumentsAtom";
import { MyPlannDrawer } from "web_myplann/Shared/Components/MyPlannDrawer";
import { DateFormat } from "web_myplann/Shared/Utils/dateFormats";
import { clampLinesSx } from "web_myplann/Shared/Utils/sxUtils";

type Props = {
	item: DocumentDTO;
	activeTag?: string;
};

export const DocumentItem = ({ item, activeTag }: Props) => {
	const theme = useTheme();
	const { t } = useResource();
	const downloadedDocuments = useAtomValue(downloadedDocumentsState);

	const [isDrawerOpen, setIsDrawerOpen] = useState(false);
	const [isDrawerConcealed, setIsDrawerConcealed] = useState(false);

	const { mutate: openDocument, isPending: isOpening } =
		useOpenDocumentMutation();

	const handleOpenDrawer = () => {
		setIsDrawerOpen(true);
		setIsDrawerConcealed(false);
	};

	const handleCloseDrawer = () => {
		setIsDrawerOpen(false);
	};

	const handleOpenDocument = () => {
		if (isOpening) {
			return;
		}
		openDocument({ id: item.id, fallbackTitle: item.title });
	};

	return (
		<Stack key={item.id}>
			<Stack
				direction="row"
				alignItems="center"
				justifyContent="space-between"
				py={2}
				px={1.6}
				onClick={handleOpenDocument}
			>
				<Stack direction="row" alignItems="center" gap={2} minWidth={0}>
					<Box flexShrink={0}>
						{isOpening ? (
							<CircularProgress size={theme.iconSize.lg} />
						) : (
							<FileText
								color={theme.palette.text.primary}
								size={theme.iconSize.lg}
							/>
						)}
					</Box>
					<Stack minWidth={0}>
						<Typography
							fontSize={15}
							fontWeight={500}
							width="100%"
							sx={clampLinesSx(2)}
						>
							{item.title}
						</Typography>

						<Stack direction="row" alignItems="center" gap={0.4}>
							{!downloadedDocuments.documents.includes(item.filename) && (
								<CircleArrowDown
									size={theme.iconSize.sm}
									color={theme.palette.primary.main}
								/>
							)}
							<Typography color={theme.palette.deprecated.grey[500]}>
								{formatDate(new Date(item.createdAt ?? ""), DateFormat.Display)}
							</Typography>
						</Stack>
					</Stack>
				</Stack>
				<Stack pl={1}>
					<IconButton
						edge="end"
						onClick={(e) => {
							e.stopPropagation();
							handleOpenDrawer();
						}}
					>
						<Ellipsis
							size={theme.iconSize.sm}
							color={theme.palette.deprecated.grey[500]}
						/>
					</IconButton>
				</Stack>
			</Stack>
			<MyPlannDrawer
				open={isDrawerOpen}
				onClose={handleCloseDrawer}
				title={t(Resources.Portfolio.List.DocumentActionsDrawer.title)}
				concealed={isDrawerConcealed}
			>
				<DocumentActionsDrawer
					item={item}
					onClose={handleCloseDrawer}
					onOverlayOpen={() => setIsDrawerConcealed(true)}
					activeTag={activeTag}
					isDownloaded={downloadedDocuments.documents.includes(item.filename)}
				/>
			</MyPlannDrawer>
		</Stack>
	);
};
